<template>
  <FlexCol gap-1 class="relative" ref="triggerRef">
    <Label :label="label" :required="required" :error="error" v-if="label" />
    <Flex gap-1 justifyBetween>
      <div class="h-[42px]">
        <IconInput icon="fa-calendar-days" @click="toggle" class="border rounded-[2px]" />
      </div>
      <Input v-model="localValue[0]" :error="error" placeholder="Inicio" :disabled="disabled" mask="##/##/####" class="w-full" />
      <Input v-model="localValue[1]" :error="error" placeholder="Fim" :disabled="disabled" mask="##/##/####" class="w-full" />
      <div class="h-[42px]">
        <IconInput :disabled="disabled" icon="fa-xmark" class="border rounded-[2px]" @click="clear()" v-if="localValue" />
      </div>
    </Flex>
    <!--  Controle de Erros -->
    <Error :message="error" v-if="error" />
    <Teleport to="body">
      <div v-if="open" :style="popupStyle" class="fixed z-[2000]">
        <Calendar range v-model="date" />
      </div>
    </Teleport>
  </FlexCol>
</template>

<script setup>
import { watch, ref, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useUtils } from '@/composables/useUtils'
import { useFormat } from '@/composables/useFormat'
const { formatBrDateToIso, formatIsoDatetime, formatDate } = useFormat()
const { isValidDateBR } = useUtils()

const props = defineProps({
  label: String,
  required: Boolean,
  disabled: Boolean,
  error: Array,
  modelValue: [Array, String],
  placeholder: String,
})

const emits = defineEmits(['update:modelValue'])
const localValue = ref([])

const error = ref(props.error)
const open = ref(false)
const date = ref(null)
const triggerRef = ref(null)
const popupStyle = ref({})

const updatePopupPosition = () => {
  const triggerElement = triggerRef.value?.$el ?? triggerRef.value
  if (!open.value || !triggerElement || typeof triggerElement.getBoundingClientRect !== "function") return
  const rect = triggerElement.getBoundingClientRect()
  popupStyle.value = {
    top: `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  }
}

const toggle = () => {
  open.value = !open.value
  if (open.value) {
    nextTick(updatePopupPosition)
  }
}

const clear = () => {
  localValue.value = []
  date.value = []
  emits('update:modelValue', [])
}

onMounted(() => {
  window.addEventListener('resize', updatePopupPosition)
  window.addEventListener('scroll', updatePopupPosition, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePopupPosition)
  window.removeEventListener('scroll', updatePopupPosition, true)
})

watch(
  date,
  (newValue) => {
    if (Array.isArray(newValue)) {
      const [start, end] = newValue
      const result = []
      if (start) {
        const startDate = new Date(start)
        startDate.setHours(0, 0, 0, 0)
        result[0] = formatIsoDatetime(startDate)
      }
      if (end) {
        const endDate = new Date(end)
        endDate.setHours(23, 59, 59, 999)
        result[1] = formatIsoDatetime(endDate)
      }

      emits('update:modelValue', result)
      if (result.length === 2) {
        toggle()
      }
    }
  },
  { deep: true },
)

watch(
  () => props.modelValue,
  (newValue) => {
    if (Array.isArray(newValue)) {
      if (newValue[0]) {
        localValue.value[0] = formatDate(newValue[0])
      }
      if (newValue[1]) {
        localValue.value[1] = formatDate(newValue[1])
      }
    }
  },
  { deep: true, immediate: true },
)

watch(
  () => localValue.value,
  (newValue) => {
    if (Array.isArray(newValue)) {
      const newDate = []
      if (newValue[0]) {
        if (newValue[0].length === 10 && isValidDateBR(newValue[0])) {
          newDate[0] = formatBrDateToIso(newValue[0], '00:00:00')
        } else if (newValue[0].length === 10 && !isValidDateBR(newValue[0])) {
          localValue.value[0] = null
        }
      }
      if (newValue[1]) {
        if (newValue[1].length === 10 && isValidDateBR(newValue[1])) {
          newDate[1] = formatBrDateToIso(newValue[1], '23:59:59')
        } else if (newValue[1].length === 10 && !isValidDateBR(newValue[1])) {
          localValue.value[1] = null
        }
      }
      if (newDate[0] && newDate[1]) {
        emits('update:modelValue', newDate)
      }
      if (newValue.length == 0) {
        emits('update:modelValue', null)
      }
    }
  },
  { deep: true, immediate: true },
)

watch(
  () => props.error,
  (newValue) => {
    error.value = newValue
  },
)

watch(open, (isOpen) => {
  if (isOpen) {
    nextTick(updatePopupPosition)
  }
})
</script>
